import { CartLinesUpdateEvent } from '@shopify/events'

const REQUEST_TIMEOUT = 30000
const TRANSACTION_KEY = 'gigglepal_buy_product_transaction_v1'
const TRANSACTION_VERSION = 1
const TRANSACTION_MAX_AGE = 24 * 60 * 60 * 1000

let isProcessing = false
let isRestoring = false

function getRoute(name, fallback) {
  return typeof Theme !== 'undefined' && Theme.routes?.[name] ? Theme.routes[name] : fallback
}

function getButton(options) {
  if (!options.documentId) return null
  return document.getElementById(options.documentId)
}

function validateOptions(options) {
  if (!options || typeof options !== 'object') {
    throw new TypeError('Invalid product options')
  }

  if (!options.buyId) {
    throw new Error('Missing required field: buyId')
  }

  const quantity = Number(options.quantity ?? 1)
  if (!Number.isInteger(quantity) || quantity < 1) {
    throw new Error('Quantity must be a positive integer')
  }
}

function setButtonState(button, text, disabled) {
  if (!button) return
  if (text) button.textContent = text
  button.disabled = disabled
}

async function fetchWithTimeout(url, options = {}) {
  const controller = new AbortController()
  const timeoutId = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT)

  try {
    return await fetch(url, {
      ...options,
      signal: controller.signal,
    })
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('Request timed out. Please try again.')
    }
    throw error
  } finally {
    window.clearTimeout(timeoutId)
  }
}

async function parseResponse(response, fallbackMessage) {
  let result = null

  try {
    result = await response.json()
  } catch {
    // Some Shopify endpoints can return an empty response.
  }

  if (!response.ok) {
    throw new Error(result?.description || result?.message || fallbackMessage)
  }

  return result
}

function buildCartItem(options, isDirectBuy = false) {
  const item = {
    id: options.buyId,
    quantity: Number(options.quantity ?? 1),
  }

  if (options.selling_plan) {
    item.selling_plan = options.selling_plan
  }

  if (isDirectBuy || options.buyType) {
    item.properties = {
      _dibs_preorder: options.buyType === 'dibs',
    }
  }

  return item
}

async function getCart() {
  const response = await fetchWithTimeout('/cart.js', {
    headers: {
      Accept: 'application/json',
    },
  })

  return parseResponse(response, 'Unable to load cart')
}

async function clearCart() {
  const response = await fetchWithTimeout('/cart/clear.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  })

  return parseResponse(response, 'Unable to clear cart')
}

async function addCartItem(item) {
  const response = await fetchWithTimeout(getRoute('cart_add_url', '/cart/add.js'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(item),
  })

  return parseResponse(response, 'Unable to add item to cart')
}

// 使用 Shopify 批量加购能力，一次请求恢复全部原购物车商品。
async function addCartItems(items) {
  const response = await fetchWithTimeout(getRoute('cart_add_url', '/cart/add.js'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({ items }),
  })

  return parseResponse(response, 'Unable to add items to cart')
}

function buildCartUpdateLines(cart, options = {}) {
  if (options.lines) return options.lines

  if (options.action === 'add' && options.buyId) {
    return [
      {
        merchandiseId: String(options.buyId),
        quantity: Number(options.quantity ?? 1),
      },
    ]
  }

  return cart.items.map((item) => ({
    id: item.key,
    quantity: item.quantity,
  }))
}

async function dispatchCartUpdate(source, options = {}) {
  const cart = await getCart()
  const deferredEventPromise = CartLinesUpdateEvent.createPromise()
  const action = options.action || 'update'
  const context = options.context || 'cart'
  const lines = buildCartUpdateLines(cart, options)

  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action,
      context,
      lines,
      promise: deferredEventPromise.promise,
    })
  )

  const detail = {
    items: cart.items,
    itemCount: cart.item_count,
    source,
    didError: false,
  }

  deferredEventPromise.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
    detail: {
      ...detail,
      lines,
    },
  })

  document.dispatchEvent(
    new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: cart,
        data: {
          itemCount: detail.itemCount,
          source: detail.source,
        },
      },
    })
  )
}

function getStoredTransaction() {
  const rawTransaction = localStorage.getItem(TRANSACTION_KEY)
  if (!rawTransaction) return null

  try {
    const transaction = JSON.parse(rawTransaction)
    const isExpired = Date.now() - transaction.createdAt > TRANSACTION_MAX_AGE

    if (transaction.version !== TRANSACTION_VERSION || !Array.isArray(transaction.items) || isExpired) {
      localStorage.removeItem(TRANSACTION_KEY)
      return null
    }

    return transaction
  } catch {
    localStorage.removeItem(TRANSACTION_KEY)
    return null
  }
}

function storeTransaction(cart) {
  const transaction = {
    version: TRANSACTION_VERSION,
    status: 'preparing',
    createdAt: Date.now(),
    items: cart.items.map((item) => ({
      id: item.variant_id,
      quantity: item.quantity,
      properties: item.properties || {},
      selling_plan: item.selling_plan_allocation?.selling_plan?.id || null,
    })),
  }

  localStorage.setItem(TRANSACTION_KEY, JSON.stringify(transaction))
  return transaction
}

function updateTransactionStatus(transaction, status) {
  localStorage.setItem(
    TRANSACTION_KEY,
    JSON.stringify({
      ...transaction,
      status,
    })
  )
}

function isCheckoutPage() {
  const pathname = window.location.pathname
  return pathname.startsWith('/checkout') || pathname.startsWith('/checkouts') || pathname.startsWith('/thank_you')
}

async function restoreCart() {
  if (isRestoring || isCheckoutPage()) return

  const transaction = getStoredTransaction()
  if (!transaction) return

  isRestoring = true

  try {
    await clearCart()

    if (transaction.items.length > 0) {
      try {
        await addCartItems(transaction.items)
      } catch (error) {
        console.warn('Unable to restore cart items in batch, falling back to individual requests:', error)

        // 批量请求失败时可能已添加部分商品，重新清空后再逐项恢复，避免重复。
        await clearCart()

        for (const item of transaction.items) {
          try {
            await addCartItem(item)
          } catch (itemError) {
            console.warn('Unable to restore cart item:', item.id, itemError)
          }
        }
      }
    }

    localStorage.removeItem(TRANSACTION_KEY)
    await dispatchCartUpdate('buy_product_restore', {
      action: 'update',
      context: 'cart',
    })
  } catch (error) {
    console.error('Unable to restore cart:', error)
  } finally {
    isRestoring = false
  }
}

async function addToCart(options) {
  if (isProcessing) return

  const button = getButton(options)
  const originalText = options.buttonText?.trim() || button?.textContent || ''

  try {
    validateOptions(options)
    isProcessing = true
    setButtonState(button, 'Adding...', true)

    await addCartItem(buildCartItem(options))
    await dispatchCartUpdate('buy_product_add', {
      action: 'add',
      context: 'product',
      buyId: options.buyId,
      quantity: options.quantity,
    })
    setButtonState(button, 'Added', true)

    window.setTimeout(() => setButtonState(button, originalText, false), 1200)
  } catch (error) {
    console.error('Unable to add product to cart:', error)
    alert(error.message || 'Unable to add item to cart')
    setButtonState(button, originalText, false)
  } finally {
    isProcessing = false
  }
}

async function buyNow(options) {
  if (isProcessing) return

  const button = getButton(options)
  const originalText = options.buttonText?.trim() || button?.textContent || ''
  let transaction = null

  try {
    validateOptions(options)
    isProcessing = true
    setButtonState(button, 'Processing...', true)

    const cart = await getCart()
    transaction = storeTransaction(cart)

    await clearCart()
    await addCartItem(buildCartItem(options, true))
    updateTransactionStatus(transaction, 'checkout_pending')

    window.location.href = '/checkout'
  } catch (error) {
    console.error('Unable to buy product:', error)

    if (transaction) {
      await restoreCart()
    }

    alert(error.message || 'Unable to start checkout. Please try again.')
    setButtonState(button, originalText, false)
  } finally {
    isProcessing = false
  }
}

window.buyProduct = {
  addToCart,
  buyNow,
}

// defer 脚本执行时 DOM 已可用，立即恢复可减少返回网站后的等待时间。
restoreCart()
window.addEventListener('pageshow', restoreCart)
